import React, { useMemo } from 'react';
import { Formik, Field, ErrorMessage, FieldProps } from 'formik';
import { Button, Checkbox, Form, Grid, Header, Message, Segment, Select } from 'semantic-ui-react';
import { Dropdown } from 'formik-semantic-ui';
import { EhsMaintenanceRoles, EhsSiteRoles, EUserLevels, EUserRoles } from '@relab/alstom-ptw-shared';

import { ServerRequestStatus } from '../../redux/types';
import { SignUpValues } from '../../interfaces';
import { TranslationComponent } from '../TranslationComponent';
import { useTranslation } from 'react-i18next';
import { CreateUserSchema } from './UserSchema';

interface UserCreateComponentProps {
  onCreateUser: (values: SignUpValues) => void;
  createUserServerRequest?: ServerRequestStatus;
}

export const UserCreateComponent = ({ onCreateUser, createUserServerRequest }: UserCreateComponentProps) => {
  const { t } = useTranslation();

  const validationSchema = useMemo(() => CreateUserSchema(t), [t]);

  const handleSubmit = (values: SignUpValues) => {
    onCreateUser(values);
  };

  const initialValues: SignUpValues = {
    name: '',
    surname: ' ',
    userLevel: EUserLevels.User,
    userRoles: [],
    email: '',
    password: '',
  };

  const handleUserRoleChange = (
    userRoles: EUserRoles[],
    setFieldValue: any,
    userRoleValue: EUserRoles,
    selected: boolean
  ) => {
    if (EhsSiteRoles.includes(userRoleValue)) {
      userRoles = userRoles.filter((uR) => !EhsSiteRoles.includes(uR));
    }

    if (EhsMaintenanceRoles.includes(userRoleValue)) {
      userRoles = userRoles.filter((uR) => !EhsMaintenanceRoles.includes(uR));
    }

    if (selected) {
      setFieldValue('userRoles', [...userRoles, userRoleValue]);
    } else {
      setFieldValue(
        'userRoles',
        userRoles.filter((uR) => uR !== userRoleValue)
      );
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ handleSubmit, isSubmitting, resetForm, values, setFieldValue }) => (
        <Form
          size="large"
          error
          loading={isSubmitting && createUserServerRequest && createUserServerRequest.pending}
          onSubmit={handleSubmit}
        >
          <Segment>
            <Header as="h2">
              <TranslationComponent stringKey={'New User'} />
            </Header>

            <Grid columns="equal">
              <Grid.Row>
                <Grid.Column>
                  <Field name="name">
                    {({ field }: FieldProps) => (
                      <Form.Input placeholder={t('Name & surname')} fluid icon="user" iconPosition="left" {...field} />
                    )}
                  </Field>
                  <ErrorMessage name="name">{(msg) => <Message error content={msg} />}</ErrorMessage>
                </Grid.Column>
                <Grid.Column />
                <Grid.Column />
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <Field name="email">
                    {({ field }: FieldProps) => (
                      <Form.Input placeholder={t('E-mail address')} fluid icon="mail" iconPosition="left" {...field} />
                    )}
                  </Field>
                  <ErrorMessage name="email">{(msg) => <Message error content={msg} />}</ErrorMessage>
                </Grid.Column>
                <Grid.Column>
                  <Field name="password">
                    {({ field }: FieldProps) => (
                      <Form.Input
                        fluid
                        icon="lock"
                        iconPosition="left"
                        placeholder={t('Password')}
                        type="password"
                        {...field}
                      />
                    )}
                  </Field>
                  <ErrorMessage name="password">{(msg) => <Message error content={msg} />}</ErrorMessage>
                </Grid.Column>
                <Grid.Column />
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <Field name="userLevel">
                    {() => (
                      <Dropdown
                        name="userLevel"
                        options={[
                          { text: t('Standard'), value: EUserLevels.User },
                          { text: t('Admin'), value: EUserLevels.Admin },
                        ]}
                      />
                    )}
                  </Field>
                </Grid.Column>
                <Grid.Column>
                  <Grid columns={'equal'}>
                    <Grid.Row>
                      <Grid.Column>
                        <Checkbox
                          label={t('PTW')}
                          checked={values.userRoles.includes(EUserRoles.Ptw)}
                          onClick={(event, data) =>
                            handleUserRoleChange(values.userRoles, setFieldValue, EUserRoles.Ptw, data.checked)
                          }
                        />
                      </Grid.Column>
                      <Grid.Column />
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column>
                        <Checkbox
                          label={t('EHS Sito')}
                          checked={
                            values.userRoles.includes(EUserRoles.EhsManager) ||
                            values.userRoles.includes(EUserRoles.EhsApplicant)
                          }
                          onClick={(event, data) =>
                            handleUserRoleChange(values.userRoles, setFieldValue, EUserRoles.EhsManager, data.checked)
                          }
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Select
                          value={
                            values.userRoles.find((userRole) => EhsSiteRoles.includes(userRole)) ??
                            EUserRoles.EhsManager
                          }
                          onChange={(event, data) =>
                            handleUserRoleChange(values.userRoles, setFieldValue, data.value as EUserRoles, true)
                          }
                          disabled={!values.userRoles.find((userRole) => EhsSiteRoles.includes(userRole))}
                          options={[
                            {
                              value: EUserRoles.EhsManager,
                              text: t('EHS Sito Manager'),
                            },
                            {
                              value: EUserRoles.EhsApplicant,
                              text: t('EHS Sito Applicant'),
                            },
                          ]}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column>
                        <Checkbox
                          label={t('EHS Maintenance')}
                          checked={
                            values.userRoles.includes(EUserRoles.EhsMaintenanceManager) ||
                            values.userRoles.includes(EUserRoles.EhsMaintenanceBasic) ||
                            values.userRoles.includes(EUserRoles.EhsMaintenanceExternalCompany) ||
                            values.userRoles.includes(EUserRoles.EhsMaintenanceExternalCompanyPlus)
                          }
                          onClick={(event, data) =>
                            handleUserRoleChange(
                              values.userRoles,
                              setFieldValue,
                              EUserRoles.EhsMaintenanceManager,
                              data.checked
                            )
                          }
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Select
                          value={
                            values.userRoles.find((userRole) => EhsMaintenanceRoles.includes(userRole)) ??
                            EUserRoles.EhsMaintenanceManager
                          }
                          onChange={(event, data) =>
                            handleUserRoleChange(values.userRoles, setFieldValue, data.value as EUserRoles, true)
                          }
                          disabled={!values.userRoles.find((userRole) => EhsMaintenanceRoles.includes(userRole))}
                          options={[
                            {
                              value: EUserRoles.EhsMaintenanceManager,
                              text: t('EHS Maintenance Manager'),
                            },
                            {
                              value: EUserRoles.EhsMaintenanceBasic,
                              text: t('EHS Maintenance Basic'),
                            },
                            {
                              value: EUserRoles.EhsMaintenanceExternalCompany,
                              text: t('EHS Maintenance External Company'),
                            },
                            {
                              value: EUserRoles.EhsMaintenanceExternalCompanyPlus,
                              text: t('EHS Maintenance External Company Plus'),
                            },
                          ]}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Grid.Column>
                <Grid.Column>
                  <Button primary fluid size="large" type="submit">
                    <TranslationComponent stringKey={'Create user'} />
                  </Button>
                  {createUserServerRequest && createUserServerRequest.success && (
                    <React.Fragment>
                      <Message content={t('User created')} />
                      <Button fluid size="large" onClick={() => resetForm()}>
                        Reset
                      </Button>
                    </React.Fragment>
                  )}
                  {createUserServerRequest && createUserServerRequest.error && (
                    <Message error content={createUserServerRequest.error} />
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Segment>
        </Form>
      )}
    </Formik>
  );
};
